"use client";

import * as React from "react";
import PerfLadder from "./PerfLadder";
import Treemap from "./viz/Treemap";
import WordCloud from "./viz/WordCloud";
import FrameBudget from "./viz/FrameBudget";
import Cadence from "./viz/Cadence";

type View = {
  id: string;
  tab: string;
  /** The question this view answers. */
  question: string;
  /** Why this encoding, and not another one. */
  why: string;
  render: () => React.ReactNode;
};

/**
 * The data-visualization proof block.
 *
 * Every view is a different D3 encoding over real data from my own work. The
 * point is not that I can draw five chart types; it is that each question
 * here gets the encoding that answers it, which is the part of the job that
 * takes judgement.
 */
const VIEWS: View[] = [
  {
    id: "budget",
    tab: "Frame budget",
    question: "Where does one 16.67 ms frame actually go?",
    why: "A stacked bar, because the question is what share of a single fixed budget each system takes and whether the total still fits. A pie would lose the ceiling; separate bars would lose the sum.",
    render: () => <FrameBudget />,
  },
  {
    id: "render",
    tab: "Render paths",
    question: "What did moving the render path buy, across the range?",
    why: "Lines, because the reader is comparing two series over an ordered quantity and the crossing point matters. The shaded band carries the worst case without needing a second chart.",
    render: () => <PerfLadder />,
  },
  {
    id: "codebase",
    tab: "Codebase",
    question: "Where does the complexity in a solo codebase actually live?",
    why: "A treemap, because this is part-to-whole across many categories at once and the sizes span two orders of magnitude. A bar chart would need a log axis and stop being readable at a glance. Line count is a poor measure of value and a decent measure of where time went, which is all this is claiming.",
    render: () => <Treemap />,
  },
  {
    id: "vocabulary",
    tab: "Vocabulary",
    question: "What does the design documentation actually talk about?",
    why: "A word cloud, which is a weak encoding for comparison and a good one for gist. Term frequency across 284 documents has no meaningful order, and the honest goal is impression rather than measurement.",
    render: () => <WordCloud />,
  },
  {
    id: "cadence",
    tab: "Cadence",
    question: "What does a year of solo development look like?",
    why: "A calendar heatmap, because the interesting structure is periodic. Position carries the date and colour carries volume, so streaks and gaps read without any axis at all.",
    render: () => <Cadence />,
  },
];

export default function DataVizLab() {
  const [active, setActive] = React.useState(VIEWS[0].id);
  const view = VIEWS.find((v) => v.id === active) ?? VIEWS[0];

  return (
    <div>
      <div
        className="seg flex-wrap"
        role="tablist"
        aria-label="Visualization views"
      >
        {VIEWS.map((v) => (
          <button
            key={v.id}
            role="tab"
            type="button"
            className="seg-item"
            aria-selected={v.id === active}
            onClick={() => setActive(v.id)}
          >
            {v.tab}
          </button>
        ))}
      </div>

      <div className="mt-4 grid gap-5 lg:grid-cols-[minmax(0,1.55fr)_minmax(0,1fr)] lg:gap-8">
        {/* Held to roughly the tallest view, so switching tabs does not
            shove the rest of the page up and down. */}
        <div className="md:min-h-[19rem]">{view.render()}</div>

        <div className="lg:pt-1">
          <p className="text-balance text-base leading-snug tracking-tight text-ink">
            {view.question}
          </p>
          <p className="eyebrow mt-5">Why this encoding</p>
          <p className="prose-body mt-2 text-[0.8125rem]">{view.why}</p>
        </div>
      </div>
    </div>
  );
}
